import { useContext } from "react";

import { InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { TextInput } from "@/DesignSystem";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { BooleanTextReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanTextReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface ICareElementBooleanTextDateReviewedProps {
    isEditable: boolean;
    label: string;
    textLabel?: string;
    side?: "row" | "column";
    element: BooleanTextReviewedCareElement;
    onChange: (element: BooleanTextReviewedCareElement) => void;
}

export function CareElementYesNoWithTextControl(props: Readonly<ICareElementBooleanTextDateReviewedProps>): JSX.Element {
    const { element, onChange, isEditable, side = "row", label, textLabel } = props;

    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    const isDatePickerVisible = !IsNullOrWhiteSpace(element.Text) || element.Value != null;

    // Compute the date reviewed based on the next state.
    // - If there is content (boolean set or non-empty text):
    //   - If no actual date value is set yet, use the encounter date.
    //   - Otherwise, keep the existing date.
    // - If both are empty/null, clear the date.
    function computeDateReviewed(nextValue: boolean | null, nextText: string): DateTimeWithPrecision {
        const hasContent = nextValue !== null || nextText.trim().length > 0;

        if (!hasContent) {
            return null;
        }

        const hasExistingDateValue = Boolean(element.DateReviewedWithPrecision?.value);
        return hasExistingDateValue ? element.DateReviewedWithPrecision : encounterDateTimeWithPrecision;
    }

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
            setFalseAsValid
            isDatePickerVisible={isDatePickerVisible}
        >
            <InlineOptionsListControl.YesOrNo
                id={`care-element-yes-no-text-${element?.Name}`}
                value={element.Value}
                disabled={!isEditable}
                onChange={option => {
                    const nextValue = element?.Value !== option.Key ? option.Key : null;
                    const nextText = element.Text ?? "";
                    const nextDate = computeDateReviewed(nextValue, nextText);
                    onChange({ ...element, Value: nextValue, DateReviewedWithPrecision: nextDate });
                }}
            />
            <TextInput
                id={`care-element-text-${element?.Name}`}
                label={textLabel}
                value={element.Text ?? ""}
                disabled={!isEditable}
                onChange={e => {
                    const newText = e.target.value;
                    const nextValue = element.Value;
                    const nextDate = computeDateReviewed(nextValue, newText);
                    onChange({
                        ...element,
                        Text: newText,
                        DateReviewedWithPrecision: nextDate
                    });
                }}
                maxLength={200}
                width="180px"
            />
        </CareElementDateReviewedRow>
    );
}
